import { createSlice, PayloadAction, Dispatch } from '@reduxjs/toolkit';
import * as authService from 'services';
import * as localStorage from 'utils/localStorage';
import { AuthState, APIError, AuthTokens, PortalUser } from 'models';
import type { RootState } from 'store/store';
import { addUserRoleToDataLayer } from 'utils/roles';

export const defaultState: AuthState = {
  session: {
    isAuthenticated: false,
    portalUser: null,
    accessToken: null,
    refreshToken: null,
  },
  isLoading: false,
  error: null,
  authCheck: false,
  authExpired: false,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: defaultState,
  reducers: {
    authStart: (state) => {
      state.isLoading = true;
    },
    authCheck: (state) => {
      state.authCheck = true;
    },
    authExpired: (state) => {
      state.authExpired = true;
    },
    logoutStart: (state) => {
      state.isLoading = true;
    },
    logoutSuccess: () => {
      localStorage.setItem('session', null);
      return defaultState;
    },
    validateTokenStart: (state, action: PayloadAction<AuthTokens>) => {
      const { accessToken, refreshToken } = action.payload;

      localStorage.setItem('session', action.payload);

      state.session.accessToken = accessToken;
      state.session.refreshToken = refreshToken;
      state.isLoading = true;
    },
    validateTokenSuccess: (state, action: PayloadAction<PortalUser>) => {
      const user = action.payload;

      state.session.portalUser = user;
      state.session.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
    },
    validateTokenFail: (state, action: PayloadAction<APIError>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

// Export selector
export const authSelector = (state: RootState) => state.auth;

// Export actions
export const {
  authStart,
  authCheck,
  authExpired,
  logoutStart,
  logoutSuccess,
  validateTokenStart,
  validateTokenSuccess,
  validateTokenFail,
} = authSlice.actions;

// Export reducer
export const authReducer = authSlice.reducer;

// Thunks
export function checkSession() {
  return async (dispatch: Dispatch) => {
    const tokens: AuthTokens | null = localStorage.getItem('session');
    if (tokens) {
      dispatch(validateTokenStart(tokens));
      try {
        const { attributes } = await authService.getUserInfo();
        addUserRoleToDataLayer(attributes);
        dispatch(validateTokenSuccess(attributes));
      } catch (e: any) {
        dispatch(validateTokenFail(e));
      }
    }
    dispatch(authCheck());
  };
}

export function logout() {
  return async (dispatch: Dispatch) => {
    dispatch(logoutStart());

    await authService.logout();

    dispatch(logoutSuccess());
    dispatch(authCheck());
  };
}

export function validateToken(accessToken: string, refreshToken: string) {
  return async (dispatch: Dispatch) => {
    dispatch(validateTokenStart({ accessToken, refreshToken }));
    try {
      const { attributes } = await authService.getUserInfo();
      dispatch(validateTokenSuccess(attributes));
    } catch (e: any) {
      dispatch(validateTokenFail(e));
    }
  };
}
